import type { DelimitedBounds } from "../content/csv/delimited-rows.js";
import { longestCandidate } from "./import-columns.js";

/**
 * How much of a vocabulary list this site will read in one go.
 *
 * **These bound the work rather than defend the door.**
 * `src/firewall/import-rules.ts` puts a size constraint on the request body at
 * the edge and a rate limit in front of the route, and `ImportAllowance` bounds
 * what one reader may start. Those stop somebody posting a megabyte. What is
 * here decides how much of a body that arrived is turned into candidates, which
 * is the cost this handler pays whatever the edge let through.
 *
 * **A paste is free and a file is Pro 专业.** The line is drawn at where the
 * bytes come from and not at how many there are, so a reader with no membership
 * can bring a list in on the day they arrive. #705 is the upload.
 *
 * **Only the free number is a product line.** {@link freeImportRows} is a
 * decision about what an account without a membership does. The number beside
 * it is a ceiling on what one request can do before it runs out of time, and it
 * is not there to hold members back.
 */

/**
 * How many records a paste carries for a reader with no membership.
 *
 * Three hundred, the size of HSK 1 and the same number as `freeSavedWords`, so
 * an HSK 1 word list fills a free account in one paste. #1075 set HSK 1 as the
 * rule for every free limit, and `free-limits.iso.test.ts` holds this to the
 * committed word list. It was two hundred, a chapter of a textbook, when the
 * list held a thousand and filling it from pastes was meant to take five.
 *
 * **The body at the edge is the bound a long paste meets first.**
 * `importBodyBytes` in `src/firewall/import-rules.ts` holds a form body to
 * sixteen kilobytes, which is about 550 bare headwords and about 200 rows of a
 * CSV this site exported with its readings and tags. Three hundred headwords
 * fit. A longer export is what the file upload #705 added is for.
 */
export const freeImportRows = 300;

/**
 * How many records one request resolves for a member.
 *
 * **Not a product line, and not a limit on how much a member may import.** A
 * member's list holds twenty thousand words and nothing here is meant to stop
 * them filling it. What this bounds is the work one synchronous request does,
 * and the reason it exists at all is that staging asks the 词典 about every
 * writing before it answers. Five thousand of those, twenty-five at a time, is
 * already at the edge of the handler's ten seconds.
 *
 * **A paste cannot reach it anyway.** `importBodyBytes` caps the request body
 * at the edge, and Chinese in a form-encoded body costs nine bytes a character,
 * so sixteen kilobytes is a few hundred short headwords. The body is the real
 * limit on a paste and this is the limit behind it.
 *
 * **A large export belongs to #705 and this must not bound it.** An Anki
 * collection of five or ten thousand notes arrives as a file, and #705 resolves
 * it in a processor Lambda with fifteen minutes rather than in the request that
 * uploaded it. That path sets its own bound, sized to that budget. Reusing this
 * number there would put a synchronous ceiling on an asynchronous job.
 */
export const proImportRows = 5_000;

/**
 * How many words an accept writes inside the request that pressed it.
 *
 * **The `/user/` handler has ten seconds and this is what fits in them.** Every
 * word an import saves is a saved word item and at least one tagging, sent
 * twenty-five to a `BatchWriteItem`, so five hundred words is around forty
 * round trips against a budget that also has to read the candidates, ask the
 * table what the reader already holds and answer the browser.
 *
 * **Above it the writing is handed to a Lambda of its own**, which
 * `ImportAccepting` explains and `ImportSettling` performs. That path has
 * fifteen minutes, so the number here is not a limit on what a reader may
 * import. It is the line above which they are shown a page that reloads itself
 * instead of a page that never arrives.
 *
 * **It is well clear of what a reader can choose row by row.** The review
 * screen draws at most `ciPageSize` controls, so an accept naming particular
 * rows is a hundred words at the most and is always written inside the request.
 * Only accepting a whole import can cross this.
 */
export const inlineImportWords = 500;

/**
 * How many columns of one record are kept.
 *
 * A vocabulary list has a handful. A spreadsheet somebody exported without
 * looking has as many as the sheet was wide, and every one of them is walked
 * and judged before it is refused.
 */
const importFields = 20;

/**
 * How many characters of one cell are kept.
 *
 * Wider than {@link longestCandidate} on purpose. A cell cut at the length of a
 * headword would look like a headword, and `importColumns` would take a column
 * of sentences for a column of words. Cut here at six times that, a sentence
 * arrives still obviously a sentence and the column is refused for what it is.
 */
const importFieldLength = longestCandidate * 6;

/**
 * How long an import holds words nobody has decided about.
 *
 * Seven days, which is long enough for a reader to come back to a tab they left
 * open on Friday. Accepting or discarding takes the candidates away at once, so
 * this only ever collects the imports somebody walked away from.
 */
export const stagedImportLifetimeMs = 7 * 24 * 60 * 60 * 1000;

/**
 * How long the record of an import stands.
 *
 * Thirty days, matching the tombstones a removal writes. The record is what
 * carries the undo, so this is also how long a reader has to take a whole
 * import back out in one press. After it the words it brought in are theirs
 * like any others and go one at a time.
 *
 * **Only the record goes.** The words stay, and so does the tag the import put
 * on every one of them, so a reader's own tag list still gathers them long
 * after the import that brought them in has gone.
 *
 * **The file behind an import expires separately, and this is not what does
 * it.** The item holds counts, a tag and a word saying where the list came
 * from, and never the bytes. #565 keeps those in a bucket of their own under a
 * lifecycle rule, on the same thirty days and for a stronger reason, an
 * uploaded file being whatever the reader had on their machine.
 */
export const importRecordLifetimeMs = 30 * 24 * 60 * 60 * 1000;

/**
 * What bounds a read of a list a reader brought in.
 *
 * **The member's ceiling is an argument because it belongs to the caller.**
 * {@link proImportRows} is what one synchronous request can resolve, and the
 * processor behind an uploaded file has fifteen minutes rather than ten
 * seconds, so it passes `uploadImportRows` instead. The free number is not an
 * argument, being a decision about what an account without a membership does
 * wherever the list came from.
 * @param pro - Whether the reader holds Pro 专业.
 * @param proRows - How many records a member's list is read to. The synchronous
 * ceiling unless the caller can afford more.
 * @returns The bounds `delimitedRows` reads under.
 */
export function importBoundsFor(
  pro: boolean,
  proRows: number = proImportRows,
): DelimitedBounds {
  return {
    rows: pro ? proRows : freeImportRows,
    fields: importFields,
    fieldLength: importFieldLength,
  };
}
